"use client";

import { Tabs as ArkTabs } from "@ark-ui/react/tabs";
import { insetFocusRing, tabFocusRing } from "@decibeltrade/web-core/recipes";
import { ReactNode, useState } from "react";
import { css, cx } from "styled-system/css";
import { flex } from "styled-system/patterns";

import {
  horizontalOverflowShadowContainerStyles,
  horizontalOverflowShadowScrollerStyles,
} from "@/recipes/overflow";

export interface Tab<TId extends string = string> {
  /** A unique ID to identify the tab */
  id: TId;
  /**
   * A `ReactNode` to be rendered in the tab trigger, usually just a `string` but a `JSX.Element`
   * may be useful if you want to render an icon next to the label text
   */
  trigger: ReactNode;
  /**  A `ReactNode` to be rendered when the tab is activated. */
  content: ReactNode;
  /** If `true`, the tab will be disabled and prevented from activating */
  disabled?: boolean;
}

export interface TabsProps<TId extends string = string> {
  /** An array that describes the available tabs */
  tabs: Array<Tab<TId>>;
  /** An optional element that will be rendered on the right-side of the tabs list */
  rightSideContent?: ReactNode;
  /** The ID of the currently activated tab. Useful for creating a controlled tabs component. */
  value?: TId;
  /** A callback to be invoked when a tab is activated. Useful for creating a controlled tabs component. */
  onChange?: (value: TId) => void;
  /**  When `true`, the tabs will stretch to fill the width of the container. */
  stretchTabs?: boolean;
  /** When `true`, the tab triggers will not be rendered. */
  hideTriggers?: boolean;
  /**
   * When `true`, the tab content will only be mounted when the tab is activated and will be
   * unmounted when the tab is deactivated (ie. the user switches tabs).
   *
   * This is useful as a performance optimization for tabs that have a lot of content or
   * content that is expensive to render.
   */
  mountOnlyWhenVisible?: boolean;
  /** Class name for applying additional styles to the tabs container */
  className?: string;
  /** Class name for applying additional styles to the tabs triggers */
  triggersClassName?: string;
  /** Class name for applying additional styles to the tabs content */
  contentClassName?: string;
  /** Test ID */
  testId?: string;
}

/** A component for implementing tabbed navigation interfaces */
export function Tabs<TId extends string = string>({
  tabs,
  rightSideContent,
  value,
  onChange,
  stretchTabs,
  hideTriggers,
  mountOnlyWhenVisible,
  className,
  triggersClassName,
  contentClassName,
  testId,
}: TabsProps<TId>) {
  const [selectedTabId, setSelectedTabId] = useState(
    value ?? tabs.find((tab) => !tab.disabled)?.id,
  );

  return (
    <TabRoot
      value={value ?? selectedTabId}
      onValueChange={(e) => {
        const newValue = e.value as TId;
        setSelectedTabId(newValue);
        onChange?.(newValue);
      }}
      lazyMount={mountOnlyWhenVisible}
      unmountOnExit={mountOnlyWhenVisible}
      className={className}
      data-testid={testId}
    >
      {!hideTriggers && (
        <TabTriggers
          tabs={tabs}
          rightSideContent={rightSideContent}
          stretchTabs={stretchTabs}
          triggersClassName={triggersClassName}
        />
      )}
      {tabs.map((tab) => (
        <TabContent key={tab.id} tab={tab} contentClassName={contentClassName}>
          {tab.content}
        </TabContent>
      ))}
    </TabRoot>
  );
}

const TabRoot = ({ children, className, ...props }: ArkTabs.RootProps) => {
  return (
    <ArkTabs.Root className={cx(rootStyles, className)} {...props}>
      {children}
    </ArkTabs.Root>
  );
};

const TabContent = ({
  tab,
  contentClassName,
  children,
}: {
  tab: Omit<Tab, "content">;
  contentClassName?: string;
  children?: ReactNode;
}) => {
  return (
    <ArkTabs.Content
      key={tab.id}
      value={tab.id}
      className={cx(
        insetFocusRing({ rounded: "0" }),
        css({ h: "full", overflow: "auto" }),
        contentClassName,
      )}
    >
      {children}
    </ArkTabs.Content>
  );
};

const TabTriggers = ({
  tabs,
  rightSideContent,
  stretchTabs,
  triggersClassName,
}: {
  tabs: Array<Omit<Tab, "content">>;
  rightSideContent?: ReactNode;
  stretchTabs?: boolean;
  triggersClassName?: string;
}) => {
  return (
    <div className={cx(tabListWrapperStyles, triggersClassName)}>
      <div
        className={cx(
          horizontalOverflowShadowContainerStyles,
          !!rightSideContent && shadowInsetForRightSideContentStyles,
        )}
      >
        <ArkTabs.List className={cx(tabListStyles, horizontalOverflowShadowScrollerStyles)}>
          {tabs.map((tab) => (
            <ArkTabs.Trigger
              key={tab.id}
              value={tab.id}
              disabled={tab.disabled}
              className={cx(tabTriggerStyles, tabFocusRing(), stretchTabs && css({ flex: "1" }))}
            >
              {tab.trigger}
            </ArkTabs.Trigger>
          ))}
          {/* Ark measures the selected trigger (--width), which stays correct even when
              stretched tabs end up unequal because one label is wider than its share. */}
          <ArkTabs.Indicator className={tabIndicator} />
        </ArkTabs.List>
      </div>
      {rightSideContent && <div className={css({ ml: "auto" })}>{rightSideContent}</div>}
    </div>
  );
};

Tabs.Root = TabRoot;
Tabs.Content = TabContent;
Tabs.Triggers = TabTriggers;

const rootStyles = css({
  display: "flex",
  flexDirection: "column",
  h: "full",
  w: "full",
});

const tabListWrapperStyles = css({
  position: "relative",
  display: "grid",
  gridTemplateColumns: "minmax(0, 1fr) auto",
  w: "full",
  flexShrink: 0,
  shadow: "[inset 0 -1px 0 0 {colors.border.primary}]",
});

const tabListStyles = flex({
  position: "relative",
  w: "full",
  overflow: "auto",
});

// Shortens the right-edge scroll shadow to match the visual height of the
// button-sized `rightSideContent` (~24px) inside the ~40px tab row, so the
// shadow doesn't tower over the controls it sits next to. Values derived from
// `tabTriggerStyles` (p:12 + highlight.md line-height 16 → 40px) minus the
// column-settings trigger's height (py:4 + line-height 16 → 24px).
const shadowInsetForRightSideContentStyles = css({
  "&::after": {
    top: "8!",
    bottom: "8!",
  },
});

const tabTriggerStyles = css({
  cursor: "pointer",
  whiteSpace: "nowrap",
  textStyle: "highlight.md",
  color: "text.tertiary",
  p: "12",
  shadow: "[inset 0 0 0 0 {colors.border.primary}, 0 0 0 0 {colors.border.primary}]",
  transition:
    "[color {durations.100} ease, font-weight {durations.100} ease, box-shadow {durations.100} ease]",

  _hover: {
    color: "text.secondary",
  },
  _selected: {
    color: "text.secondary",
  },
  _disabled: {
    color: { base: "text.disabled", _hover: "text.disabled" },
    cursor: "not-allowed",
  },
});

const tabIndicator = css({
  height: "[2px]",
  width: "[var(--width)]",
  bottom: "0",
  bg: "text.secondary",
});
